import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SEO_INVENTORY, getTotalInventoryCount } from '../data/seoInventory';
import LastReviewedStamp from '../components/seo/LastReviewedStamp';
import { BreadcrumbsContainer } from '../components/seo/Breadcrumbs';
import useJsonLd from '../hooks/useJsonLd';

/**
 * SitemapPage - Public HTML sitemap at /sitemap
 *
 * Why this exists (SEO):
 *   1. Indexation rate booster. The largest single lever for BuildPilot
 *      right now. ~400 programmatic pages currently have no inbound
 *      internal link from the main nav, so Google deprioritises them.
 *      A single crawlable index page that links every URL fixes that.
 *   2. AI-search citation surface. ChatGPT, Perplexity, Claude and
 *      Gemini grounding crawls prefer one URL they can iterate through.
 *   3. User-friendly fallback. Power users use sitemaps to find
 *      buried content faster than navigation.
 *
 * Data source: /data/seoInventory.js (single source of truth, also
 * used by Footer.jsx).
 */
const SitemapPage = () => {
  const total = getTotalInventoryCount();
  const groups = Object.entries(SEO_INVENTORY);

  // CollectionPage schema for AI crawlers + Google
  useJsonLd(
    [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': 'https://buildpilot.com.au/sitemap',
        url: 'https://buildpilot.com.au/sitemap',
        name: `BuildPilot Sitemap - ${total}+ Pages on Home Building in Adelaide`,
        description:
          `Browse every guide, calculator, suburb page, builder comparison and resource on BuildPilot. ${total}+ pages organised into ${groups.length} topical sections.`,
        isPartOf: { '@id': 'https://buildpilot.com.au#website' },
        mainEntity: {
          '@type': 'ItemList',
          numberOfItems: total,
          itemListElement: groups.flatMap(([, links]) =>
            links
              .filter((l) => !l.external)
              .map((l, idx) => ({
                '@type': 'ListItem',
                position: idx + 1,
                url: `https://buildpilot.com.au${l.href}`,
                name: l.name,
              }))
          ),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://buildpilot.com.au' },
          { '@type': 'ListItem', position: 2, name: 'Sitemap', item: 'https://buildpilot.com.au/sitemap' },
        ],
      },
    ],
    'sitemap-page'
  );

  return (
    <div className="min-h-screen bg-white" data-testid="sitemap-page">
      <Helmet>
        <title>BuildPilot Sitemap - All Pages Index (2026) | Adelaide Home Building Resource</title>
        <meta
          name="description"
          content={`Complete sitemap of BuildPilot's ${total}+ pages covering Adelaide home builders, designs, costs, suburbs, knockdown rebuilds, house & land packages, and the full workspace tool suite.`}
        />
        <meta name="robots" content="index, follow, max-snippet:-1" />
        <link rel="canonical" href="https://buildpilot.com.au/sitemap" />
        <meta property="og:title" content="BuildPilot Sitemap - All Pages Index (2026)" />
        <meta property="og:description" content={`Every guide, calculator, comparison and resource on BuildPilot - ${total}+ pages.`} />
        <meta property="og:url" content="https://buildpilot.com.au/sitemap" />
        <meta property="og:type" content="website" />
      </Helmet>

      <BreadcrumbsContainer
        items={[{ name: 'Sitemap' }]}
        variant="light"
      />

      <header className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-8 sm:pb-12">
        <h1 className="text-[32px] sm:text-[44px] lg:text-[54px] font-bold text-[#1d1d1f] leading-[1.05] tracking-tight max-w-3xl">
          Every page on BuildPilot, in one place.
        </h1>
        <p className="text-[15px] sm:text-[17px] text-[#5a5a5f] mt-4 sm:mt-5 max-w-2xl leading-relaxed">
          {total}+ pages covering Adelaide home builders, designs, suburb guides, real cost benchmarks,
          knockdown rebuilds, house &amp; land packages, suppliers, tradies, professional services
          and the full workspace tool suite - organised into {groups.length} sections.
        </p>
        <div className="mt-4">
          <LastReviewedStamp date="2026-03-01" className="!text-gray-500" />
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {groups.map(([groupTitle, links]) => (
            <section key={groupTitle} data-testid={`sitemap-group-${groupTitle.toLowerCase().replace(/\s+/g, '-')}`}>
              <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#1d1d1f] mb-4 pb-2 border-b border-gray-200">
                {groupTitle}
                <span className="ml-2 text-gray-400 font-normal normal-case tracking-normal">
                  ({links.length})
                </span>
              </h2>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[14px] text-gray-700 hover:text-[#1d8efe] hover:underline transition-colors"
                      >
                        {link.name} ↗
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-[14px] text-gray-700 hover:text-[#1d8efe] hover:underline transition-colors"
                        data-testid={`sitemap-link-${link.href.replace(/[^a-z0-9]+/gi, '-').slice(0, 50)}`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <aside className="mt-20 p-6 sm:p-8 bg-gray-50 rounded-2xl border border-gray-100">
          <h2 className="text-[15px] sm:text-[17px] font-semibold text-[#1d1d1f] mb-2">
            Looking for the machine-readable sitemap?
          </h2>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#5a5a5f] leading-relaxed">
            Search engines and crawlers should use the XML sitemap at{' '}
            <a
              href="/sitemap.xml"
              className="text-[#1d8efe] hover:underline font-medium"
              data-testid="sitemap-xml-link"
            >
              /sitemap.xml
            </a>
            . Dynamic live inventory feeds are available at{' '}
            <a
              href="/api/packages/sitemap.xml"
              className="text-[#1d8efe] hover:underline font-medium"
            >
              /api/packages/sitemap.xml
            </a>{' '}
            (house &amp; land packages) and{' '}
            <a
              href="/api/home-designs/sitemap.xml"
              className="text-[#1d8efe] hover:underline font-medium"
            >
              /api/home-designs/sitemap.xml
            </a>{' '}
            (home designs). AI grounding crawlers can use{' '}
            <a href="/llms.txt" className="text-[#1d8efe] hover:underline font-medium">
              /llms.txt
            </a>
            .
          </p>
        </aside>
      </main>
    </div>
  );
};

export default SitemapPage;
